'use client';

/**
 * Vertical short-form video feed — the YouTube Shorts / TikTok pattern
 * as a prop-driven site-builder block.
 *
 * <h3>How the active slide is decided</h3>
 *
 * The scroller is the single source of truth. Slides are exactly one
 * scroller-height tall with {@code scroll-snap-type: y mandatory}, so
 * "which slide is active" is whatever the browser snapped to — we
 * observe rather than drive it. An {@code IntersectionObserver} rooted
 * at the scroller (threshold 0.6) reports the slide that owns the
 * viewport and that index becomes {@code activeIndex}. Every input —
 * touch swipe, wheel, arrow button, keyboard — does the same thing:
 * scroll the container. None of them set {@code activeIndex} directly,
 * so the highlighted slide can never disagree with what's on screen
 * (the classic bug when you track index and scroll position separately).
 *
 * <h3>How the DOM stays small</h3>
 *
 * All N slides exist as empty positioned boxes — they have to, or the
 * scroll height and snap points would be wrong. But a slide only mounts
 * a media element when it's inside the render window
 * ({@code |i - activeIndex| <= windowSize}, default 1 → previous /
 * current / next). Everything outside renders its poster and nothing
 * else. See {@link ShortsCard} for the per-kind rules (native videos
 * mount across the window and buffer; YouTube iframes mount only while
 * active).
 *
 * <h3>Order</h3>
 *
 * Authored order by default. With {@code shuffle} on, a Fisher–Yates
 * permutation is drawn once per page load, client-side — see the
 * shuffle effect for why it can't happen during SSR.
 *
 * <h3>Height</h3>
 *
 * In `viewport` mode the feed is `100dvh` minus whatever chrome sits
 * above it — measured at runtime, because the shell exposes no general
 * header-height variable. A flat `100dvh` would push the page past the
 * viewport and clip the bottom of every slide. Authors can pin the
 * allowance with {@code viewportOffset} when they know it.
 *
 * <h3>Autoplay policy</h3>
 *
 * The feed starts muted by default because that's the only thing
 * browsers reliably allow. If an operator turns {@code startMuted} off,
 * the first {@code play()} rejection flips the whole feed back to muted
 * and retries, so a blocked autoplay degrades to a silent-but-playing
 * video rather than a frozen poster. The sound toggle then works, since
 * by then the viewer has clicked.
 *
 * <h3>Styling</h3>
 *
 * Inline styles + a scoped {@code <style>} tag with design-token CSS
 * vars, like every other block in this package — Tailwind is not
 * available here (the package is host-agnostic and the admin canvas
 * bundles it through Vite without the portal's Tailwind pipeline).
 */

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronUp, ChevronDown, Volume2, VolumeX, Play, Pause } from 'lucide-react';
import { tx, asList } from '../_shared/coerce';
import { SectionShell } from '../_shared/SectionShell';
import { EmptyState } from '../_shared/EmptyState';
import { useMounted } from '../_shared/useMounted';
import type { PaddingBox } from '../_shared/resolvePadding';
import { ShortsCard, type ShortsItem } from './ShortsCard';

export type { ShortsItem } from './ShortsCard';

type Localized = string | Record<string, string>;

export interface ShortsFeedProps {
  title?: Localized;
  eyebrow?: Localized;
  /** The authored playlist. */
  videos?: ShortsItem[];
  /**
   * Play the videos in a random order, reshuffled on every page load.
   * Applied on the client after mount — a shuffle picked during SSR
   * wouldn't match the one the client picks.
   */
  shuffle?: boolean;
  /** `viewport` = the visible area under the site header; `fixed` = `height` px. */
  heightMode?: 'viewport' | 'fixed';
  /** Slide height in px when {@code heightMode} is `fixed`. */
  height?: number;
  /**
   * How much vertical space the chrome above the feed takes, subtracted
   * from the viewport in `viewport` mode.
   *
   * <p>`auto` (default) measures it — see {@code chromeOffset} below.
   * Pass any CSS length to pin it instead (`'88px'`, or a var like
   * `'var(--cinematic-header-height, 88px)'`), or `'0'` to force a true
   * edge-to-edge 100dvh feed on a page with no header.
   */
  viewportOffset?: string;
  /** How the video fills its 9:16 stage. */
  fit?: 'cover' | 'contain';
  /** Start muted. Leave on unless you know a gesture precedes the feed. */
  startMuted?: boolean;
  /** Loop each clip. Off means the feed auto-advances when one ends. */
  loop?: boolean;
  showArrows?: boolean;
  showCaptions?: boolean;
  /** Slide dots down the right edge. */
  showProgress?: boolean;
  /** Neighbours to keep mounted on each side. 1 = prev/current/next. */
  windowSize?: number;
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
  style?: CSSProperties;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

/**
 * Fisher–Yates permutation of `[0..n)`. Exported (not barrelled) so the
 * shuffle can be tested deterministically with a seeded `random` — the
 * component itself can only be observed through SSR, which by design
 * never shuffles.
 *
 * Every index appears exactly once, so a shuffled feed can't drop or
 * duplicate a video however the draw falls.
 */
export function shufflePermutation(n: number, random: () => number = Math.random): number[] {
  const out = Array.from({ length: n }, (_, i) => i);
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

export function ShortsFeed({
  title,
  eyebrow,
  videos,
  shuffle = false,
  heightMode = 'viewport',
  height = 640,
  viewportOffset = 'auto',
  fit = 'cover',
  startMuted = true,
  loop = true,
  showArrows = true,
  showCaptions = true,
  showProgress = true,
  windowSize = 1,
  paddingOuter,
  paddingInner,
  style,
}: ShortsFeedProps) {
  // asList tolerates the shapes the backend can hand back for an array
  // prop (null, a single object, a JSON string) instead of crashing.
  const { t } = useTranslation('site-builder-blocks');
  const authored = useMemo(() => asList<ShortsItem>(videos), [videos]);
  const scope = `sf-${useId().replace(/[:]/g, '')}`;
  const mounted = useMounted();
  const heading = tx(title);
  const kicker = tx(eyebrow);

  const scrollerRef = useRef<HTMLDivElement>(null);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [activeIndex, setActiveIndex] = useState(0);
  const [muted, setMuted] = useState(startMuted);
  const [paused, setPaused] = useState(false);
  // The feed itself is on screen in the *page*. Separate from which slide
  // is active inside the scroller — a `fixed` height feed scrolled past
  // must stop making noise, and playing video no one can see is pure
  // bandwidth. Starts true so the first slide plays before the observer's
  // first callback lands.
  const [feedVisible, setFeedVisible] = useState(true);
  // Measured height of everything stacked above the feed (site header,
  // category strip, any block the operator dropped in first). Null until
  // measured — see the effect below.
  const [chromeOffset, setChromeOffset] = useState<number | null>(null);

  const win = clamp(Math.round(windowSize) || 1, 0, 4);

  // ── Shuffle ───────────────────────────────────────────────────────────────
  //
  // Client-side only, and deliberately so: a random order picked during
  // SSR would differ from the one the client picks, React would see the
  // hydration mismatch and throw the whole subtree away. So the server
  // renders the authored order and the shuffle lands in the first
  // post-mount effect — before any player mounts (see `playersReady`),
  // so the viewer never sees the unshuffled first video load.
  //
  // A permutation of indices rather than a copy of the array: it's what
  // we actually need, and it keeps the items themselves untouched.
  const [order, setOrder] = useState<number[] | null>(null);

  useEffect(() => {
    if (!shuffle || authored.length < 2) {
      setOrder(null);
      return;
    }
    // Drawn once per mount (and if the playlist length or the toggle
    // changes) — NOT on every render, or the feed would reorder under
    // the viewer's thumb mid-scroll.
    setOrder(shufflePermutation(authored.length));
  }, [shuffle, authored.length]);

  const items = useMemo(
    () => (order ? order.map((i) => authored[i]).filter(Boolean) : authored),
    [order, authored],
  ) as ShortsItem[];

  // Hold players back until the order is settled, so a shuffled feed
  // doesn't briefly load the authored first video and then swap it.
  const playersReady = mounted && (!shuffle || authored.length < 2 || order !== null);

  // ── Fit the viewport MINUS the site chrome ────────────────────────────────
  //
  // A flat 100dvh is wrong on any page with a header: header + feed then
  // exceeds the viewport, the page itself grows a scrollbar, and the
  // bottom of every slide is cut off. The shell publishes no general
  // header-height variable (only `--cinematic-header-height`, and only in
  // overlay mode), so we measure instead — which also picks up the
  // category strip, sidebar top bar, and anything else above us, without
  // the feed needing to know those exist.
  //
  // The measurement is the scroller's offset from the top of the
  // *document*, not the viewport, so it doesn't change with scroll
  // position. It can't feed back into itself either: an element's own
  // height doesn't move its own top edge.
  useEffect(() => {
    if (heightMode !== 'viewport' || viewportOffset !== 'auto') return;
    const el = scrollerRef.current;
    if (!el) return;

    const measure = () => {
      const top = el.getBoundingClientRect().top + window.scrollY;
      // A feed sitting deep in a long page isn't "under the header" in
      // any useful sense — subtracting a huge offset would leave a
      // sliver. Past half a viewport, treat it as full-height.
      setChromeOffset(top > 0 && top < window.innerHeight * 0.5 ? Math.round(top) : 0);
    };

    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    // Chrome above us can change height after we mount (a header that
    // hydrates, a font that swaps, a collapsing sidebar). Watch it rather
    // than trusting the first measurement.
    const observer = new ResizeObserver(measure);
    observer.observe(document.body);

    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      observer.disconnect();
    };
  }, [heightMode, viewportOffset]);

  // ── Active slide: observed, never assumed ──────────────────────────────────
  // Rooted at the scroller so the page's own scroll position is
  // irrelevant — the feed works identically at the top of a long page or
  // half-scrolled past it.
  useEffect(() => {
    const root = scrollerRef.current;
    if (!root || items.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.6) continue;
          const idx = Number((entry.target as HTMLElement).dataset.index);
          if (Number.isFinite(idx)) setActiveIndex(idx);
        }
      },
      // 0.6 with mandatory snapping means exactly one slide can qualify
      // at rest, and mid-flick nothing does — so the index changes once,
      // when the new slide has clearly won.
      { root, threshold: [0.6] },
    );

    // Drop refs left behind by a longer previous playlist.
    slideRefs.current.length = items.length;
    for (const el of slideRefs.current) if (el) observer.observe(el);
    return () => observer.disconnect();
  }, [items.length]);

  // Page-level visibility of the whole feed.
  useEffect(() => {
    const root = scrollerRef.current;
    if (!root) return;
    const observer = new IntersectionObserver(
      ([entry]) => setFeedVisible(!!entry && entry.intersectionRatio >= 0.25),
      { threshold: [0, 0.25] },
    );
    observer.observe(root);
    return () => observer.disconnect();
  }, []);

  // Same reasoning for a backgrounded tab: browsers throttle it but don't
  // reliably stop audio, and coming back to a clip that ran on without you
  // is worse than coming back to a paused one.
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) setPaused(true);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // A new active slide always starts playing — pausing is per-viewing,
  // not a mode the feed stays stuck in.
  useEffect(() => {
    setPaused(false);
  }, [activeIndex]);

  const scrollToIndex = useCallback(
    (idx: number) => {
      const root = scrollerRef.current;
      if (!root) return;
      const next = clamp(idx, 0, items.length - 1);
      // Slides are exactly one scroller-height tall, so the target offset
      // is arithmetic — no scrollIntoView, which would also scroll the
      // page and fight the snap container.
      root.scrollTo({ top: next * root.clientHeight, behavior: 'smooth' });
    },
    [items.length],
  );

  const goNext = useCallback(() => scrollToIndex(activeIndex + 1), [activeIndex, scrollToIndex]);
  const goPrev = useCallback(() => scrollToIndex(activeIndex - 1), [activeIndex, scrollToIndex]);

  // ── Desktop wheel: one slide per gesture ───────────────────────────────────
  // Native snapping treats a trackpad flick as one long scroll and can
  // fly past several slides. We take over, but only while there's a
  // slide to move to — at the ends the event passes through so the page
  // keeps scrolling and the feed doesn't trap the viewer.
  useEffect(() => {
    const root = scrollerRef.current;
    if (!root) return;

    let locked = false;
    let unlock = 0;

    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) < 8) return;
      const forward = e.deltaY > 0;
      const target = forward ? activeIndex + 1 : activeIndex - 1;
      if (target < 0 || target > items.length - 1) return;

      e.preventDefault();
      if (locked) return;
      locked = true;
      unlock = window.setTimeout(() => {
        locked = false;
      }, 520);
      scrollToIndex(target);
    };

    root.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      root.removeEventListener('wheel', onWheel);
      window.clearTimeout(unlock);
    };
  }, [activeIndex, items.length, scrollToIndex]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case 'ArrowDown':
      case 'PageDown':
        e.preventDefault();
        goNext();
        break;
      case 'ArrowUp':
      case 'PageUp':
        e.preventDefault();
        goPrev();
        break;
      case ' ':
        e.preventDefault();
        setPaused((p) => !p);
        break;
      case 'm':
      case 'M':
        setMuted((m) => !m);
        break;
      default:
    }
  };

  // Unmuted autoplay was refused. Go muted and let the card's effect
  // re-run — the clip plays silently instead of sitting on a poster.
  const onAutoplayBlocked = useCallback(() => setMuted(true), []);

  // Only meaningful when `loop` is off; a looping clip never ends.
  const onEnded = useCallback(() => {
    if (loop) return;
    if (activeIndex < items.length - 1) goNext();
  }, [loop, activeIndex, items.length, goNext]);

  if (items.length === 0) {
    return (
      <SectionShell
        title={heading}
        kicker={kicker}
        paddingOuter={paddingOuter}
        paddingInner={paddingInner}
        style={style}
        maxWidth="full"
      >
        <EmptyState title={t('shortsFeed.empty.title')} hint={t('shortsFeed.empty.hint')} />
      </SectionShell>
    );
  }

  // The chrome allowance, as a CSS length. An explicit `viewportOffset`
  // wins; otherwise use what we measured. Before the first measurement
  // (SSR and the hydration render) it's '0px', so the server and client
  // agree — the real value lands in the first post-mount effect.
  const offset = viewportOffset !== 'auto' ? viewportOffset || '0px' : `${chromeOffset ?? 0}px`;

  // Two declarations, not one: `dvh` tracks the mobile URL bar as it
  // hides, and the `vh` line above it keeps engines without `dvh` on a
  // sane value instead of dropping the rule entirely.
  const viewportHeightCss = `height: calc(100vh - ${offset}); height: calc(100dvh - ${offset});`;
  const slideHeight = heightMode === 'viewport' ? null : `${clamp(height, 240, 2000)}px`;

  const css = `
    .${scope}-scroller {
      position: relative;
      ${slideHeight ? `height: ${slideHeight};` : viewportHeightCss}
      overflow-y: auto;
      overflow-x: hidden;
      scroll-snap-type: y mandatory;
      /* Stop the page behind the feed from rubber-banding when a swipe
         runs past the last slide. */
      overscroll-behavior-y: contain;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
      -ms-overflow-style: none; 
      border-radius: ${heightMode === 'viewport' ? '0' : 'var(--radius-lg, 12px)'};
      outline: none;
    }
    .${scope}-scroller::-webkit-scrollbar { display: none; width: 0; height: 0; }
    .${scope}-scroller:focus-visible {
      box-shadow: 0 0 0 2px var(--color-brand-500, #6366f1);
    }
    .${scope}-slide {
      position: relative;
      height: 100%;
      width: 100%;
      scroll-snap-align: start;
      /* Never let a fast flick skip a slide. */
      scroll-snap-stop: always;
      display: flex;
      justify-content: center;
    }
    /* The 9:16 stage. On phones it just fills the slide; on desktop it
       stays portrait and centred, letterboxed by the black slide. */
    .${scope}-stage {
      position: relative;
      height: 100%;
      /* Phone: both dimensions set, so aspect-ratio is ignored and the
         stage goes full-bleed — which is what a phone feed should do. */
      width: 100%;
      overflow: hidden;
    }
    @media (min-width: 640px) {
      /* Desktop: release the width and aspect-ratio derives it from the
         height, giving a real portrait stage on a black slide. */
      .${scope}-stage { width: auto; aspect-ratio: 9 / 16; max-width: 100%; }
    }
    .${scope}-ctl {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 42px;
      height: 42px;
      border-radius: 999px;
      border: 1px solid rgba(255, 255, 255, 0.22);
      background: rgba(0, 0, 0, 0.42);
      backdrop-filter: blur(8px);
      color: #fff;
      cursor: pointer;
      padding: 0;
      font: inherit;
      transition: background 160ms ease, transform 160ms ease, opacity 160ms ease;
    }
    .${scope}-ctl:hover { background: rgba(0, 0, 0, 0.62); transform: scale(1.05); }
    .${scope}-ctl:disabled { opacity: 0.32; cursor: default; transform: none; }
    .${scope}-ctl:focus-visible {
      outline: 2px solid var(--color-brand-500, #6366f1);
      outline-offset: 2px;
    }
    .${scope}-rail {
      position: absolute;
      right: max(12px, env(safe-area-inset-right));
      top: 50%;
      transform: translateY(-50%);
      z-index: 3;
      display: flex;
      flex-direction: column;
      gap: 10px;
      align-items: center;
    }
    .${scope}-dots {
      position: absolute;
      left: max(8px, env(safe-area-inset-left));
      top: 50%;
      transform: translateY(-50%);
      z-index: 2;
      display: flex;
      flex-direction: column;
      gap: 5px;
      pointer-events: none;
    }
    .${scope}-dot {
      width: 3px;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.32);
      height: 12px;
      transition: height 180ms ease, background 180ms ease;
    }
    .${scope}-dot[data-on='true'] { height: 22px; background: #fff; }
    @media (prefers-reduced-motion: reduce) {
      .${scope}-scroller { scroll-behavior: auto; }
      .${scope}-ctl { transition: none; }
    }
  `;

  return (
    <SectionShell
      title={heading}
      kicker={kicker}
      paddingOuter={paddingOuter}
      paddingInner={paddingInner}
      style={style}
      maxWidth="full"
    >
      <style dangerouslySetInnerHTML={{ __html: css }} />

      <div style={{ position: 'relative' }}>
        <div
          ref={scrollerRef}
          className={`${scope}-scroller`}
          tabIndex={0}
          role="region"
          aria-roledescription="carousel"
          aria-label={heading || t('shortsFeed.ariaLabel')}
          onKeyDown={onKeyDown}
        >
          {items.map((item, i) => {
            const windowed = Math.abs(i - activeIndex) <= win;
            return (
              <div
                key={i}
                data-index={i}
                ref={(el) => {
                  slideRefs.current[i] = el;
                }}
                className={`${scope}-slide`}
                aria-roledescription="slide"
                aria-label={`${i + 1} / ${items.length}`}
                aria-hidden={i !== activeIndex}
              >
                <div className={`${scope}-stage`}>
                  <ShortsCard
                    item={item}
                    active={i === activeIndex}
                    windowed={windowed}
                    muted={muted}
                    // Off-screen pauses rather than deactivates, so the
                    // clip resumes where the viewer left it instead of
                    // rewinding to 0 like a slide change does.
                    paused={paused || !feedVisible}
                    loop={loop}
                    fit={fit}
                    mounted={playersReady}
                    showCaption={showCaptions}
                    onAutoplayBlocked={onAutoplayBlocked}
                    onEnded={onEnded}
                    onToggle={() => setPaused((p) => !p)}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {showProgress && items.length > 1 && (
          <div className={`${scope}-dots`} aria-hidden>
            {items.map((_, i) => (
              <span key={i} className={`${scope}-dot`} data-on={i === activeIndex} />
            ))}
          </div>
        )}

        <div className={`${scope}-rail`}>
          <button
            type="button"
            className={`${scope}-ctl`}
            onClick={() => setMuted((m) => !m)}
            aria-label={muted ? t('shortsFeed.unmute') : t('shortsFeed.mute')}
            aria-pressed={muted}
          >
            {muted ? <VolumeX size={19} /> : <Volume2 size={19} />}
          </button>
          <button
            type="button"
            className={`${scope}-ctl`}
            onClick={() => setPaused((p) => !p)}
            aria-label={paused ? t('shortsFeed.play') : t('shortsFeed.pause')}
          >
            {paused ? <Play size={19} /> : <Pause size={19} />}
          </button>
          {showArrows && items.length > 1 && (
            <>
              <button
                type="button"
                className={`${scope}-ctl`}
                onClick={goPrev}
                disabled={activeIndex === 0}
                aria-label={t('shortsFeed.previousVideo')}
              >
                <ChevronUp size={20} />
              </button>
              <button
                type="button"
                className={`${scope}-ctl`}
                onClick={goNext}
                disabled={activeIndex >= items.length - 1}
                aria-label={t('shortsFeed.nextVideo')}
              >
                <ChevronDown size={20} />
              </button>
            </>
          )}
        </div>
      </div>
    </SectionShell>
  );
}
